import React from 'react';
import { 
  Box, 
  Typography, 
  Stack
} from '@mui/material';
import { CaseManageView } from '../../../../services/CaseManagementService/types/caseManageTypes';
import { EncounterType } from '../../../../services/EncounterService/types/encounterTypes';
import SectionPaper from '../../../common/components/styled-ui-elements/SectionPaper';

interface PatientInfoViewProps {
  caseData?: CaseManageView;
  encounterData?: EncounterType;
}

const PatientInfoView: React.FC<PatientInfoViewProps> = ({ caseData, encounterData }) => {
  return (
    <SectionPaper sx={{ p: 3, mb: 3 }}>
      {/* Patient Summary */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
            Patient Name (Account #)
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {caseData || encounterData?.patient ? 
              `${(caseData?.firstName || encounterData?.patient?.firstName || '')} ${(caseData?.lastName || encounterData?.patient?.lastName || '')} (${caseData?.accountNumber || 'N/A'})` 
              : 'Loading...'}
          </Typography>
        </Box>
        
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
            Date of Birth
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {encounterData?.patient?.birthDate ? 
              new Date(encounterData.patient.birthDate).toLocaleDateString() : 'N/A'}
          </Typography>
        </Box>
        
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
            Service Date
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {caseData?.serviceDate || encounterData?.encounterInfo?.serviceDate ? 
              new Date(caseData?.serviceDate || encounterData?.encounterInfo?.serviceDate!).toLocaleDateString() : 'N/A'}
          </Typography>
        </Box>
        
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
            Physician
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {caseData?.physicianName || 
              (encounterData?.physician?.firstName && encounterData?.physician?.lastName ? 
                `${encounterData.physician.firstName} ${encounterData.physician.lastName}` : 
                (encounterData?.physician ? String(encounterData.physician) : 'N/A'))}
          </Typography>
        </Box>
      </Stack>

      {/* Insurance Information */}
      <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
          Insurance Information
        </Typography>
        {encounterData?.insurances && encounterData.insurances.length > 0 ? (
          <Stack spacing={2}>
            {encounterData.insurances.map((insurance, index) => (
              <Stack key={index} direction="row" spacing={3} sx={{ 
                p: 2, 
                backgroundColor: '#ffffff',
                borderRadius: 1,
                border: '1px solid #e2e8f0'
              }}>
                <Box sx={{ minWidth: '80px' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Priority
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {insurance.priority || 'N/A'}
                  </Typography>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Payer Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {insurance.payerName || 'N/A'}
                  </Typography>
                </Box>
                
                <Box sx={{ minWidth: '150px' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Policy
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {insurance.policyNumber || 'N/A'}
                  </Typography>
                </Box>
                
                <Box sx={{ minWidth: '120px' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Group
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {insurance.groupNumber || 'N/A'}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Typography variant="body1" color="text.secondary">No insurance information available</Typography>
        )}
      </Box>
    </SectionPaper>
  );
};

export default PatientInfoView;
